import sanitizeHtml from "sanitize-html";
import { Condition, SuccessPageRule } from "../components/FormClient/FormClient.types";
import { Field, StepData } from "../components/FormViewer/FormViewer.types";
import { SessionLayer } from "../interfaces";


/**
 * Check wether rendering client side or server side
 * @returns {Boolean} true if rendering client side
 */

export const sanitizeContent = (content: string) => {
  return sanitizeHtml(content, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      "*": [ "style" ]
    },
    // Additive: the defaults (http/https/ftp/mailto) strip `tel:` links. Allowing
    // tel only permits something previously removed — no existing content relies
    // on tel being stripped — so other forms are unaffected. Needed for the
    // Mictera "call the practice" link (DTX-300).
    allowedSchemes: sanitizeHtml.defaults.allowedSchemes.concat(["tel"]),
    parseStyleAttributes: false
  });
};

export const isBrowser = () => window !== undefined;

export const fetchGAClientId = () => {
  const measurementId = "G-KG72SNLJZP";

  const checkAndFetch = () => {
    if (typeof window.gtag === "function") {
      window.gtag("get", measurementId, "client_id", (clientId: string) => {
        if (clientId) {
          window.sessionLayer.lead.marketing_data.ga_client_id = clientId;
        }
      });
      return true;
    }
    return false;
  };

  // Try fetching immediately
  if (checkAndFetch()) return;

  // Retry every second until gtag is available (max 10 tries)
  let attempts = 0;
  const interval = setInterval(() => {
    if (checkAndFetch() || attempts >= 10) {
      clearInterval(interval);
    }
    attempts++;
  }, 1000);
};

export const trackEvent = (eventLabel: string, fieldLabel?: string) => {
  const { formName, uuid } = window.sessionLayer?.lead?.form_client ?? {};

  const event: {
    event: string;
    form: { formName: string | undefined; uuid: string | undefined };
    [key: string]: string | object | undefined;
  } = {
    event: eventLabel,
    form: { formName, uuid }
  };

  if (fieldLabel) {
    event["fieldLabel"] = fieldLabel;
  }

  window.dataLayer?.push(event);
};

export async function getAppBFFUrl() {
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }
  
  const defaultLocale = "de-DE";
  return new Promise<string>((resolve) => {
    const waitForConfig = setInterval(() => {
      if (window.FC_CONFIG) {
        const { lead: { form_client: { locale } } } = window.sessionLayer as { lead: SessionLayer };
        const actualLocale: string = locale ?? defaultLocale;

        clearInterval(waitForConfig);
        const baseUrlObject: { [key: string]: string } = window.FC_CONFIG.BASE_URL;
        resolve(baseUrlObject[actualLocale] ?? baseUrlObject[defaultLocale]);
      }
    }, 50);
  });
}

export async function getPublicBucketUrl() {
  if (process.env.REACT_APP_PUBLIC_BUCKET_URL) {
    return process.env.REACT_APP_PUBLIC_BUCKET_URL;
  }
  return new Promise<string>((resolve) => {
    const waitForConfig = setInterval(() => {
      if (window.FC_CONFIG) {
        clearInterval(waitForConfig);
        const baseUrlObject: string = window.FC_CONFIG.PUBLIC_BUCKET_URL;
        resolve(baseUrlObject);
      }
    }, 50);
  });
}

export const getStorageValue = (key: string, defaultValue: string) => {
  const initial: string | null = localStorage.getItem(key);
  return initial || defaultValue;
}

export const redirectToSuccessPage = (defaultSuccessPageUrl: string, rules: Array<SuccessPageRule>, lead_data: any) => {
  if (rules.length === 0) {
    // If there are no rules, redirect to the default success page
    window.location.href = defaultSuccessPageUrl;
    return;
  }

  // Iterate through the rules
  for (const rule of rules) {
    // Check if all conditions in this rule are met
    const conditionsMet = rule.conditions.every(condition => {
      const { field, operator, value } = condition;

      const fieldValue = lead_data[field];
      const hasMatch = field in lead_data;

      return evaluateCondition(operator, hasMatch, fieldValue, value);
    });

    // If all conditions in this rule are met, perform the redirection
    if (conditionsMet) {
      const currentParams = new URLSearchParams(window.location.search);
      const successPageUrl = new URL(rule.successPageUrl);

      // Append any query parameters from the current URL that are not already in the success page URL
      currentParams.forEach((value, key) => {
        if (!successPageUrl.searchParams.has(key)) {
          successPageUrl.searchParams.append(key, value);
        }
      });

      // Redirect to the success page URL
      window.location.href = successPageUrl.toString();
      return;
    }
  }

  window.location.href = defaultSuccessPageUrl;
};

export const getFieldsFormSession = (fieldCrmId: string): string => {
  if (!fieldCrmId) return '';

  const { lead } = window.sessionLayer as { lead: SessionLayer };

  let fieldValue = '';

  // Search in lead_data
  lead?.lead_data?.some((step: StepData) => {
    const { fields, answer } = step;
    if (fields) {
      const matchedField = fields.find((field: Field) => field.field_crm_id === fieldCrmId);
      if (matchedField) {
        fieldValue = matchedField.field_value;
        return true;
      }
    }
    if (answer && (step.settings.crm_field === fieldCrmId || step.label === fieldCrmId)) {
      fieldValue = answer.field_value;
      return true;
    }
    return false;
  });

  // If not found, search in push_data
  if (!fieldValue) {
    lead?.push_data?.some((f: any) => {
      if (f.crm_field === fieldCrmId) {
        fieldValue = f.crm_value;
        return true;
      }
      return false;
    });
  }

  return fieldValue;
};

export const replaceVariablesInText = (text: string, replacement?: string) => {
  if (text) {
    return text.replace(/\$\{\{(.*?)\}\}/g, (_, variableName) => {
      return replacement ?? getFieldsFormSession(variableName);
    });
  }
  return text;
}

export const getVariableFromText = (text: string): string => {
  if (!text) return '';
  return text.replace(/\$\{\{(.*?)\}\}/g, (_, variableName) => variableName.trim());
};

export const getMatchingFieldsFromSession = (conditions: Array<any>) => {

  if (!conditions || conditions.length === 0) return [];
  const { lead } = window.sessionLayer as { lead: SessionLayer };
  const matchingFields: any = [];

  lead?.lead_data?.forEach((step: StepData) => {
    const { fields, answer } = step;
    if (fields) {
      const matchedFields = fields.filter((field: Field) => {
        return returnIfConditionsMatch(conditions, field);
      }).map((field: Field) => {
        return {
          field_crm_id: field.field_crm_id,
          field_value: field.field_value,
          field_session_source: "lead_data"
        }
      });
      matchingFields.push(...matchedFields);
    }
    if (answer) {
      const answerField = { 
        field_crm_id: step.settings.crm_field ?? step.label, 
        field_value: answer.field_value, 
        field_session_source: "lead_data" 
      };
      if (returnIfConditionsMatch(conditions, answerField)) {
        matchingFields.push(answerField);
      }
    }
  });

  lead?.push_data?.forEach((f: any) => {
    const returnField = {
      field_crm_id: f.crm_field,
      field_value: f.crm_value,
      field_session_source: "push_data"
    }
    if (returnIfConditionsMatch(conditions, returnField)) {
      matchingFields.push(returnField);
    }
  });
  
  return matchingFields;
};

export const getFieldFromPushData = (field_crm_id: string) => {
  const { lead } = window.sessionLayer as { lead: SessionLayer };
  const matchingField = lead?.push_data?.find((field: any) => field.crm_field === field_crm_id);

  return matchingField;
}

export const returnIfConditionsMatch = (conditions: Array<Condition>, sessionField: Field) => {
  // Iterate through the conditions
  return conditions.some((condition) => {
    const { operator, value, field } = condition;
    const hasMatch = sessionField.field_crm_id === field;
    const fieldValue = hasMatch && sessionField.field_value;
    const evalOperator = operator || "exists";

    return evaluateCondition(evalOperator, hasMatch, fieldValue, value);
  });
}

export const medicalSurveyCalculatedResult = (product: string) => {
  if (!product) return;
  const medicalSurveryQuestions = [
    "calculated_medical_survey_1",
    "calculated_medical_survey_2",
    "calculated_medical_survey_3",
    "calculated_medical_survey_4",
    "calculated_medical_survey_5"
  ];

  const sum = medicalSurveryQuestions.reduce((total, key) => {
    const value = parseInt(getFieldsFormSession(key), 10);
    return total + (isNaN(value) ? 0 : value); 
  }, 0);

  let resultDescription = "";
  if (sum < 8) {
    resultDescription = "schweren";
  } else if (sum >= 8 && sum <= 11) {
    resultDescription = "moderaten";
  } else if (sum >= 12 && sum <= 16) {
    resultDescription = "milden bis moderaten";
  } else if (sum >= 17 && sum <= 21) {
    resultDescription = "milden";
  } else if (sum >= 22 && sum <= 25) {
    resultDescription = "keine";
  }

  return {
    resultValue: sum.toString(),
    resultDescription
  };
};

/**
 * ICIQ-SF (incontinence) scoring — used by the Mictera funnel.
 *
 * Distinct from medicalSurveyCalculatedResult (which scores the IIEF/ED
 * questionnaire used by Edera/Lutera). Spec: mictera_funnel_iciq_scoring_spec
 * (iciq_v2).
 *
 *  - total_score = Q1 + Q2 + Q3   (range 0–21; Q4 is NOT scored)
 *      Q1 frequency: 0–5
 *      Q2 amount:    0, 2, 4, 6
 *      Q3 impact:    0, 2, 5, 7, 10
 *  - severity band derived from total_score
 *  - incontinence type read from Q4 (iciq_type: stress | urge | mixed | none)
 *  - results copy selected from the severity × type matrix
 *
 * ⚠️ TEMP(DTX-300): the German content produced below (severity/type labels,
 * results copy, mictera_info copy + programme name) is HARD-CODED for the
 * online-marketing experiment. It belongs in DatoCMS so PMs can edit it without
 * a deploy; it lives in code only because it's a computed severity×type matrix
 * with no editable-content mechanism today. Migrate to CMS-driven content when
 * one exists, then delete this. Grep `TEMP(DTX-300)` for all experiment debt.
 */
export const ICIQ_FUNNEL_VERSION = "iciq_v2";

export const iciqCalculatedResult = () => {
  const scoredQuestions = [
    "calculated_medical_survey_1",
    "calculated_medical_survey_2",
    "calculated_medical_survey_3"
  ];

  const score = scoredQuestions.reduce((total, key) => {
    const value = parseInt(getFieldsFormSession(key), 10);
    return total + (isNaN(value) ? 0 : value);
  }, 0);

  let severityTag: string;
  let severityLabel: string;
  if (score === 0) {
    severityTag = "none";
    severityLabel = "Keine Beschwerden";
  } else if (score <= 5) {
    severityTag = "mild";
    severityLabel = "Leichte Beschwerden";
  } else if (score <= 10) {
    severityTag = "moderate";
    severityLabel = "Mäßige Beschwerden";
  } else {
    severityTag = "severe";
    severityLabel = "Starke Beschwerden";
  }

  const typeTag = (getFieldsFormSession("iciq_type") || "").toLowerCase();
  const typeLabels: Record<string, string> = {
    stress: "Belastungsinkontinenz",
    urge: "Dranginkontinenz",
    mixed: "Mischinkontinenz",
    none: "Keine Beschwerden"
  };
  const typeLabel = typeLabels[typeTag] || "";

  // Severity × type results copy — verbatim from the PM scoring & content map
  // (mictera_funnel_scoring_and_content_map §4). Shown on the results step.
  // none / score 0: Mictera is not indicated (no incontinence) — do NOT pitch
  // prevention training, state plainly that we are not a fit (Isabel, 2026-06-25).
  let copy: string;
  if (!typeTag || typeTag === "none" || score === 0) {
    copy = "Dein Ergebnis zeigt keine Anzeichen von Inkontinenz. Kranus Mictera ist für die Behandlung von Inkontinenz zugelassen — für Dein Ergebnis haben wir aktuell kein passendes Angebot.";
  } else if (typeTag === "stress") {
    if (severityTag === "moderate") {
      copy = "Belastungsinkontinenz mit deutlichen Beschwerden. Der Beckenboden verliert unter Druck die Kontrolle, strukturiertes Training ist der wirksamste Weg.";
    } else if (severityTag === "severe") {
      copy = "Ausgeprägte Belastungsinkontinenz. Die Kranus Mictera Therapie gibt dem Beckenboden die strukturierte Unterstützung, die er braucht.";
    } else {
      copy = "Deine Symptome deuten auf leichte Belastungsinkontinenz hin, typisch beim Niesen, Lachen oder Sport. Früh angesetzt wirkt gezieltes Beckenbodentraining hier direkt.";
    }
  } else if (typeTag === "urge") {
    if (severityTag === "mild") {
      copy = "Leichte Dranginkontinenz, die Blase sendet Fehlalarme. Blasentraining und mentale Übungen helfen, den Drang zu kontrollieren.";
    } else {
      copy = "Dranginkontinenz mit deutlichen Beschwerden. Reines Beckenbodentraining reicht hier nicht, die Kranus Mictera Therapie kombiniert Blasen- und Beckenbodentraining gezielt.";
    }
  } else {
    copy = "Mischinkontinenz, Belastung und Drang kombiniert. Beide Ursachen brauchen eine eigene Behandlung. Die Kranus Mictera Therapie adressiert beide gleichzeitig.";
  }

  // Type-specific "Mictera Info" (therapy-recommendation) content — verbatim
  // from the PM's NEW HTML (mictera_funnel_iciq_prescription_reachout_v2_new,
  // renderMicteraInfo), which supersedes the older HTML/PDF §5 wording.
  //  - programName: the recommended programme label ("Für dich" card)
  //  - infoCopy:    the type-specific therapy explanation
  const programNameByType: Record<string, string> = {
    stress: "Beckenbodentraining",
    urge: "Blasen- & mentales Training",
    mixed: "Kombitherapie",
    // none: no programme label — Mictera is not indicated for no-incontinence
    // results, so no "Für dich" recommendation is shown (Isabel, 2026-06-25).
    none: ""
  };
  const infoCopyByType: Record<string, string> = {
    stress: "Gezieltes Beckenbodentraining, das genau dort ansetzt, wo der Druckverlust entsteht — strukturiert, täglich, bis die Kontrolle zurückkommt.",
    urge: "Blasen- und mentales Training, das den überaktiven Drang Schritt für Schritt neu kalibriert — kombiniert und gezielt auf Deine Symptome abgestimmt.",
    mixed: "Zwei Ansätze in einem Programm — Beckenboden und überaktive Blase werden gleichzeitig behandelt.",
    none: "Kranus Mictera ist eine Therapie für Frauen mit Inkontinenz. Da Dein Ergebnis keine Beschwerden zeigt, sind wir für Dich aktuell nicht die richtige Lösung."
  };
  const programName = programNameByType[typeTag] || programNameByType.none;
  const infoCopy = infoCopyByType[typeTag] || infoCopyByType.none;

  return {
    score,
    severityTag,
    severityLabel,
    typeTag,
    typeLabel,
    copy,
    programName,
    infoCopy
  };
};

/**
 * Doctor reach-out derived values for the Mictera funnel (DTX-300).
 *
 * Reads what the funnel has collected in the session and computes the
 * prescription-outreach helpers the later steps interpolate via ${{...}}:
 *  - icd_code        ICD-10 derived from the incontinence type (iciq_type)
 *  - rx_mail_subject / rx_mail_body / rx_mail_href   prefilled e-mail (mailto)
 *  - rx_phone_symptom / rx_tel_href                  phone-call guidance
 *  - doctor_channels  comma list of channels the chosen doctor exposes
 *                     (email|phone|doctolib) — drives channel availability
 *
 * Pure read of session state, like iciqCalculatedResult — safe to recompute on
 * every relevant submit (pushToSession overwrites). Mictera-only.
 *
 * ⚠️ TEMP(DTX-300): the German reach-out content here (e-mail subject + body,
 * phone-script symptom line, ICD-10 text, PZN, Fachkreise URL) is HARD-CODED for
 * the experiment. It should be DatoCMS-driven so PMs can edit it; it lives in
 * code only because we assemble a URL-encoded mailto:/tel: payload, for which
 * there is no CMS field mechanism today. Migrate when a content source exists,
 * then delete. Grep `TEMP(DTX-300)`.
 */
const RX_PZN = "20287989";
const RX_FACHKREISE_URL = "www.kranushealth.com/de/fachkreise/mictera";

export const micteraReachoutResult = () => {
  const typeTag = (getFieldsFormSession("iciq_type") || "").toLowerCase();
  const score = getFieldsFormSession("iciq_score") || "";

  // ICD-10 by incontinence type (ported from the PM design buildIcdText()).
  let icdCode: string;
  if (typeTag === "urge") {
    icdCode = "N39.42 (Dranginkontinenz)";
  } else if (typeTag === "mixed") {
    icdCode = "N39.3 + N39.42 (Mischinkontinenz)";
  } else {
    icdCode = "N39.3 (Belastungsinkontinenz)";
  }

  const typeLabel = getFieldsFormSession("iciq_type_label") || "Beckenbodenschwäche";

  // Patient name (HubSpot firstname/lastname); falls back to a placeholder.
  const firstName = getFieldsFormSession("firstname") || "";
  const lastName = getFieldsFormSession("lastname") || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "[Dein Name]";

  const doctorEmail = getFieldsFormSession("doctor_email") || "";
  const doctorPhone = getFieldsFormSession("doctor_phone") || "";

  const mailSubject = "Rezeptanfrage: Kranus Mictera (DiGA)";
  const mailBody = [
    "Sehr geehrte Damen und Herren,",
    "",
    `mein Name ist ${fullName} und ich bin Patientin in Ihrer Praxis.`,
    "",
    `Ich habe online einen Fragebogen zur Blasenfunktion ausgefüllt (ICIQ-SF, Score: ${score}/21). Das Ergebnis weist auf mögliche Beschwerden im Bereich ${typeLabel} hin, eine ärztliche Diagnose steht noch aus.`,
    "",
    "Ich würde gerne die digitale Gesundheitsanwendung Kranus Mictera nutzen und bitte um eine Verordnung, wenn möglich ohne gesonderten Termin.",
    "",
    "Informationen zur Verordnung, die ich dazu recherchiert habe:",
    `- PZN: ${RX_PZN}`,
    `- ICD-10: ${icdCode}`,
    "- Verordnung: Kassenrezept (Muster 16) oder E-Rezept Token",
    `- Weitere Informationen für Fachkreise: ${RX_FACHKREISE_URL}`,
    "",
    "Herzliche Grüße,",
    fullName,
  ].join("\n");

  const mailHref = `mailto:${doctorEmail}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

  // Phone-call symptom line by type (ported from getPhoneSymptomLine()).
  const phoneSymptoms: Record<string, string> = {
    stress: "Ich habe online einen Fragebogen ausgefüllt und habe die Vermutung, unter Belastungsinkontinenz zu leiden (unwillkürlicher Urinverlust beim Niesen, Lachen oder Sport).",
    urge: "Ich habe online einen Fragebogen ausgefüllt und habe die Vermutung, unter Dranginkontinenz zu leiden (plötzlicher starker Harndrang mit unwillkürlichem Urinverlust).",
    mixed: "Ich habe online einen Fragebogen ausgefüllt und habe die Vermutung, unter Mischinkontinenz zu leiden (Urinverlust sowohl unter körperlicher Belastung als auch bei Harndrang).",
    none: "Ich habe Interesse an der DiGA Kranus Mictera und möchte mehr Informationen erhalten.",
  };
  const phoneSymptom = phoneSymptoms[typeTag] || phoneSymptoms.stress;
  const telHref = doctorPhone ? `tel:${doctorPhone.replace(/\s+/g, "")}` : "";

  return {
    icdCode,
    mailSubject,
    mailBody,
    mailHref,
    phoneSymptom,
    telHref,
  };
};

const evaluateCondition = (operator: string, hasMatch: boolean, fieldValue: string, value: string) => {
  switch (operator) {
    case "exists":
      return hasMatch
    case "does_not_exist":
      return !hasMatch
    case "is_exact_value":
      return fieldValue === value;
    case "is_not_exact_value":
      return fieldValue !== value;
    case "contains_value":
      return fieldValue && fieldValue.includes(value);
    case "does_not_contain_value":
      return fieldValue && !fieldValue.includes(value);
    default:
      return false; // Invalid operator, consider condition as not met
  }
}
/**
 * Bytes one upload request may carry. The public ingress rejects bodies over
 * 20 MiB; this leaves room for the form fields sent alongside the files.
 */
export const MAX_UPLOAD_BYTES = 19 * 1024 * 1024;

export const exceedsUploadLimit = (files: Array<Pick<File, "size">>): boolean =>
  files.reduce((total, file) => total + file.size, 0) > MAX_UPLOAD_BYTES;
